/**
 * Analytics Service
 *
 * Singleton service for tracking analytics events.
 * Events are stored locally in IndexedDB and synced to the server
 * periodically or when the app comes online.
 */

import axios from 'axios';
import { deviceTokenService } from '../auth/DeviceToken';
import { generateUUID } from '../../utils/uuid';
import { logRocketService } from '../logrocket';
import { logger } from '../logging/logger';
import { resolveApiBaseUrl } from '../embedded/hostConfig';
import {
  AnalyticsEvent,
  AnalyticsEventType,
  TrackEventInput,
  IAnalyticsService,
  SlotCompletionContext,
} from './types';
import analyticsDB from './AnalyticsDB';
import {
  getUnreportedCrashes,
  markCrashesReported,
} from '../logging/crashCapture';
import { detectPlatform } from '../platform/utils/detectPlatform';
import {
  isWaitingDeviceApproval,
  logAuthAwareError,
} from '../../utils/deviceApprovalState';

// Analytics events go to the Java Content API (port 8082), not the FastAPI (port 3001)
const _ANALYTICS_API_URL = resolveApiBaseUrl();

// 2026-06-11 (fix analytics flush timeout):
// - BATCH_SIZE: 50 → 25 — reduz carga do trigger DB cascateado (3 UPSERTs por
//   playback_complete via update_analytics_totals). Em batch de 50, eram ~200
//   ops dentro de 1 transação. Com 25, cai pra ~100. Menos chance de bater no
//   @Transactional(timeout=30) do backend.
// - FLUSH_TIMEOUT_MS: novo — substitui o `timeout: 30000` hardcoded no axios.
//   60s cobre cold-start + contention em dev local (admin+player no mesmo
//   backend Java). Em prod (Railway) raramente vai usar mais que 2-3s.
const BATCH_SIZE = 25;
const FLUSH_TIMEOUT_MS = 60_000;
/**
 * TETO entre tentativas de envio (Renato 2026-09-22, Fase 4 do loteamento).
 *
 * Era 5 min e servia só de rede de segurança: o ritmo real vinha do flush
 * imediato POR EVENTO de playback — medido em campo, **~10 s entre POSTs**.
 * Agora o lote fecha por {@link BATCH_TRIGGER_EVENTS} e este teto garante que
 * nada fique parado mais que isto quando o player gera pouco evento.
 *
 * A ~4,5 eventos/min por player (medido em produção em 21/09), 10 eventos levam
 * ~2,2 min — então na prática quem fecha o lote de um player ATIVO é a contagem,
 * e o teto governa só os de baixa atividade (conteúdo longo, playlist curta).
 */
const FLUSH_CEILING_MS = 2 * 60 * 1000;

/**
 * De quanto em quanto tempo o timer confere o teto.
 *
 * Deliberadamente menor que o teto: um `setInterval(TETO)` fixo dispararia em
 * batida própria, sem relação com o último envio, e mandaria 1 evento solto se o
 * lote tivesse acabado de fechar pela contagem. Aqui o timer só pergunta
 * "faz mais de {@link FLUSH_CEILING_MS} que não tento enviar?".
 */
const CEILING_CHECK_MS = 20 * 1000;

/**
 * Espalha o teto entre devices (±15%).
 *
 * Sem isto, 6.000 tablets que subiram juntos depois de um OTA em massa passam a
 * bater o teto na mesma janela, para sempre — um rebanho que nada no servidor
 * dispersa. O jitter é sorteado UMA vez por boot, não por ciclo: o objetivo é
 * separar devices entre si, não tornar o intervalo imprevisível.
 */
const CEILING_JITTER = 0.15;

/**
 * Eventos acumulados que fecham o lote na hora.
 *
 * O valor conversa com o `BATCH_SIZE` de 25 (teto do que cabe numa transação do
 * servidor sem arriscar o `@Transactional(timeout=30)`): 10 fecha bem abaixo
 * disso, deixando folga pro caso de vários eventos chegarem juntos.
 */
const BATCH_TRIGGER_EVENTS = 10;

/**
 * Tipos que IGNORAM o lote e vão na hora.
 *
 * São raros — não movem a conta de requisições — e perdem valor com atraso:
 * diagnóstico de falha e sinal de vida. `player_crash` e `memory_guard_reload`
 * são reportados quando o player já está de saída, então segurar é arriscar
 * perder. Qualquer tipo FORA desta lista entra no lote.
 */
/**
 * De quanto em quanto tempo a bateria é relida do nativo.
 *
 * Antes da Fase 4, `trackEvent` fazia `await getLiveBatteryInfo()` — import
 * dinâmico do `@capacitor/device` + travessia da ponte JNI — a CADA evento, e o
 * evento só era gravado no IndexedDB depois da resposta. Para um dado que anda
 * ~1% a cada vários minutos.
 *
 * Os consumidores toleram folga de sobra: a coluna do admin (`PlayerService`) e
 * o `dischargingTracker`, cujo alerta tem threshold de 5 min.
 */
const BATTERY_REFRESH_MS = 60_000;

/**
 * Janela de dedup do `playback_complete` (Renato + Claude 2026-10-17).
 *
 * O mesmo `mediaId` NÃO pode gerar dois `playback_complete` dentro desta janela.
 * Motivo: no motor A/B (Android, 2 decoders) há uma race entre o emit do swap
 * (`Stream/index.tsx` doSwitch ~3267, que SÓ depois marca o guard
 * `scheduledCompleteEmittedForRef` ~3265) e o emit reativo do `onEnded` do DOM
 * (`handleVideoEnded` ~4161, que CHECA o guard ~4144). Quando o `onEnded` roda
 * ANTES do swap marcar o ref (TOCTOU), os dois emitem o MESMO item — medido ao
 * vivo no Tab A7 (EMBEDDED-Renato): duplicatas com gap de ~0,03-0,04s, inflando
 * a impressão de ~360/h (teto real de 10s) para ~410/h. A TV (AVPlay, emissor
 * único via `handleAvplayEnded`) não sofre e marca 360/h certinho.
 *
 * Dedup na FONTE cobre as 3 rotas (swap / onEnded / avplay) sem depender da
 * ordem. É seguro: duas conclusões REAIS do mesmo item ficam ≥ a duração do slot
 * apart (na frota, ≥10s) — muito acima desta janela; só a duplicata (sub-100ms)
 * cai aqui.
 */
const DUP_COMPLETE_WINDOW_MS = 1_000;

const IMMEDIATE_EVENT_TYPES: ReadonlySet<AnalyticsEventType> = new Set([
  'boot',
  'error',
  'playback_error',
  'player_crash',
  'memory_guard_reload',
  'campaign_change',
]);

/**
 * Eventos de INÍCIO: desligados (Renato 2026-09-20).
 *
 * `playback_start` e `slot_started` não têm **nenhum consumidor** no servidor —
 * levantamento de 20/09/2026 sobre o banco de produção: nenhuma query, nenhum
 * trigger, nenhum relatório os lê. Os dois triggers de faturamento começam com
 * `IF NEW.event_type = 'playback_complete'`. Mesmo assim eram 43% das linhas de
 * `tab_analytics_events` (2,7 GB de 5,9 GB) e metade das escritas no IndexedDB
 * do player.
 *
 * O gate fica no emissor, não no envio: assim o evento não é sequer construído
 * — sem escrita no IndexedDB, sem delete depois do sync, sem bytes na rede.
 * Isso corta pela metade as escritas E os deletes do banco local, que é o que
 * gera tombstone (vide o episódio da RD-Raia em 20/09).
 *
 * Constante, e não flag de build: em 03/08 um caminho de build que pulava o
 * `generate-env.ts` saiu com `VITE_AVPLAY_BACKEND` errado e mutou a TV de
 * bancada. Uma `const` no módulo não tem esse buraco.
 *
 * Para religar: mude para `true` e gere OTA. O restante do caminho (método,
 * tipo, ingest, coluna) continua de pé — nada foi removido.
 */
const TRACK_PLAYBACK_START = false;
const TRACK_SLOT_STARTED = false;

/**
 * Evento `slot_completed`: desligado (Renato 2026-09-21).
 *
 * Ele era emitido **1:1** com o `playback_complete` nos três pontos do
 * `Stream` (imagem, `<video>`, AVPlay) — sempre logo depois, guardado só por
 * `if (slotCtx)`. Provado em produção na RD-Raia, mesma mídia: 2.143/2.143 em
 * 20/09 e 2.117/2.117 em 19/09.
 *
 * Dos sete campos dele, só quatro eram exclusivos (`slotIndex`, `playlistId`,
 * `programacaoId`, `tier`) e agora viajam no próprio `playback_complete`. Os
 * outros eram redundantes: `durationActual` valia o mesmo que o `duration` nos
 * três sites, e `completedNaturally` era `true` fixo. `scheduledOffsetS` nem
 * existe no DTO do servidor — era descartado no ingest.
 *
 * Isso apaga ~24% das linhas de `tab_analytics_events` e, de quebra, corrige a
 * contagem dobrada da Prova de Exibição (uma linha por exibição).
 *
 * ⚠️ O servidor precisa estar com o `PlaylistMetricsAggregator` aceitando o
 * `playback_complete` enriquecido ANTES deste OTA, senão as métricas de SLA de
 * playlist zeram nos players atualizados.
 *
 * Mantido como constante para religar por comparação se algum número estranhar.
 */
const TRACK_SLOT_COMPLETED = false;

/**
 * Rajada adaptativa pra drenar acúmulo (Renato 2026-09-20).
 *
 * Medido no Tab A7 com 44k pendentes (IndexedDB de 433 MB), via CDP:
 *
 *   getAll(limit=25)  = 25.489 ms
 *   getAll(limit=200) = 50.644 ms
 *   getAll(limit=500) = 27.657 ms
 *
 * Ler 500 custa o MESMO que ler 25 — o custo é por OPERAÇÃO (abrir transação +
 * varrer o índice numa base gigante), não por evento. Com lote fixo de 25 o
 * player drenava ~4,5 eventos/min: 10 dias pra esvaziar. Lote maior é ~20×
 * mais eficiente pelo mesmo preço no cliente.
 *
 * O teto NÃO é o cliente, é o servidor: `AnalyticsService.processBatch` roda
 * sob `@Transactional(timeout = 30)` e o trigger `update_analytics_totals`
 * dispara 3 UPSERTs cascateados por `playback_complete`. Por isso a rajada é
 * ADAPTATIVA e auto-limitada pela latência real: dobra enquanto o servidor
 * responde folgado, cai pela metade assim que passa do teto de latência.
 * Sem backlog, volta pra BATCH_SIZE — a frota saudável não muda de
 * comportamento.
 */
const BACKLOG_BATCH_MAX = 200;

/**
 * Auto-cura do banco degradado (Renato 2026-09-20).
 *
 * O IndexedDB do Chromium roda sobre LevelDB; num device fraco a compactação
 * não acompanha e o object store passa a ficar preso ~40 s de cada vez. Não
 * há API de compactar exposta ao JavaScript. Vide
 * `AnalyticsDB.closeForMaintenance`.
 *
 * O gatilho é a latência medida, não um calendário: recriar por agenda seria
 * mexer no banco sem motivo. E o sintoma separa os dois estados por três
 * ordens de grandeza — saudável lê o lote em ~5 ms, degradado em ~38.000 ms —
 * então não há zona cinzenta pra falso positivo.
 */
const DEGRADED_READ_MS = 5_000;
/** Flushes lentos seguidos antes de reconstruir — descarta contenção pontual. */
const DEGRADED_STREAK = 3;

/** Metade do `@Transactional(timeout = 30)` do servidor — margem de 2×. */
const BACKLOG_LATENCY_CEILING_MS = 15_000;
// Backoff exponencial pro retry após erro de rede. Evita o loop infinito
// "timeout → retry em 30s → timeout → retry em 30s..." que martelava o backend
// quando ele já estava sob pressão. Sequência: 30s, 60s, 120s, 300s (cap).
const RETRY_BACKOFF_SEQUENCE_MS = [30_000, 60_000, 120_000, 300_000];
/**
 * O teto de acúmulo é conferido 1× a cada N flushes, não em todos. É manutenção
 * e, durante backlog, cada ciclo gasto com ela é um lote de 25 eventos que
 * deixou de subir. Vide `AnalyticsDB.enforceRetentionLimits`.
 *
 * (Até 2026-09-22 isto dizia "purga por idade" e apontava pro
 * `deleteOldSyncedEvents`. A retenção por IDADE deixou de existir em 20/09,
 * quando virou teto por QUANTIDADE; o método por idade ficou órfão e foi
 * removido.)
 */
const PURGE_EVERY_N_FLUSHES = 20;

// CRITICAL: Changed from 'efflux_device_id' to prevent pollution of the main Hardware ID
// Analytics should track its own temp ID until the real one is set via setDeviceId()
const DEVICE_ID_KEY = 'efflux_analytics_id';

class AnalyticsService implements IAnalyticsService {
  private deviceId: string;
  private flushInterval: ReturnType<typeof setInterval> | null = null;
  /** Flushes desde a última purga por idade (vide PURGE_EVERY_N_FLUSHES). */
  private flushesSincePurge = 0;
  /**
   * Tamanho do lote do próximo flush. Fica em BATCH_SIZE em regime normal e
   * sobe (dobrando) só enquanto há acúmulo e o servidor responde folgado.
   * Vide BACKLOG_BATCH_MAX.
   */
  private nextBatchSize = BATCH_SIZE;
  /**
   * Eventos gravados desde a última tentativa de envio. É o gatilho de
   * quantidade do lote (vide BATCH_TRIGGER_EVENTS).
   *
   * ⚠️ Contador EM MEMÓRIA de propósito. A tentação é contar pelo banco —
   * varrer o índice `pending_sync` pra saber quantos faltam. Era isso que o
   * `AnalyticsDB.getPendingCount` fazia, e foi removido do caminho quente em
   * 20/09/2026 e do código em 22/09: numa base com 44k pendentes custava ~59 s
   * por ciclo, mais caro que o envio inteiro. Chamar por conteúdo
   * reintroduziria a regressão em escala pior.
   *
   * Se ele divergir do banco (flush que falhou, por exemplo), o teto de tempo
   * garante o envio de qualquer jeito, e o dreno de backlog recupera o atraso.
   */
  private eventosDesdeUltimoFlush = 0;
  /** Quando o último flush FOI TENTADO (epoch ms). Base do teto de tempo. */
  private ultimoFlushEm = Date.now();
  /**
   * Teto desta instância, já com jitter aplicado. Sorteado uma vez por boot —
   * vide CEILING_JITTER.
   */
  private readonly tetoComJitter =
    FLUSH_CEILING_MS * (1 + (Math.random() * 2 - 1) * CEILING_JITTER);
  /** Último snapshot de bateria lido do nativo. Vide BATTERY_REFRESH_MS. */
  private bateriaAtual: {
    batteryLevel: number | 'NA';
    isCharging: boolean | 'NA';
  } = {
    batteryLevel: 'NA',
    isCharging: 'NA',
  };
  /** Se a bateria já foi lida ao menos uma vez (evita 'NA' no evento de boot). */
  private bateriaJaLida = false;
  private bateriaTimer: ReturnType<typeof setInterval> | null = null;
  /** Flushes seguidos cuja leitura do lote passou de DEGRADED_READ_MS. */
  private slowReadStreak = 0;
  /**
   * Se o aviso de lentidão já foi emitido NESTE episódio de degradação.
   * Reseta quando a leitura volta ao normal.
   */
  private avisouNesteEpisodio = false;
  private isFlushing = false;
  /**
   * Contador de retries consecutivos com falha. Reset a cada flush bem-sucedido.
   * Usado pra escolher o delay no backoff exponencial (vide
   * RETRY_BACKOFF_SEQUENCE_MS). Evita martelar o backend quando ele está
   * sob pressão (contention dev local, cold-start, GC pause).
   */
  private retryAttempts = 0;
  private isInitialized = false;
  private apiUrl: string = resolveApiBaseUrl();

  /**
   * Fase 1b (escalabilidade) — hint pra reconectar o SSE de conteúdo quando o
   * server reporta `sse_connected: false` na resposta do flush (conexão SSE
   * zumbi). Injetado no boot (useSyncInit). Throttle abaixo evita martelar a
   * reconexão se vários flushes seguidos reportarem desconectado.
   */
  private sseReconnectHint: (() => void) | null = null;
  private lastSseReconnectHintAt = 0;

  constructor() {
    this.deviceId = this.getOrCreateDeviceId();
    this.init();
  }

  /**
   * Sets the device ID for analytics events.
   * Call this when the proper device ID is determined by AuthStore.
   */
  setDeviceId(id: string): void {
    if (!id) return;
    const oldId = this.deviceId;
    this.deviceId = id;
    console.log('[AnalyticsService] Device ID updated:', this.deviceId);

    // If switching from temp/invalid to valid ID, trigger flush immediately
    if (this.isTemporaryId(oldId) && !this.isTemporaryId(id)) {
      console.log('[AnalyticsService] Valid ID set, triggering buffered flush');
      // Small delay to ensure state settles
      setTimeout(() => this.flush(), 1000);
    }
  }

  /**
   * Sets the API URL for analytics events.
   * Call this when the proper API URL is determined.
   */
  setApiUrl(url: string): void {
    if (!url) return;
    this.apiUrl = url;
    console.log('[AnalyticsService] API URL updated:', this.apiUrl);
  }

  /**
   * Initializes the service (starts auto-flush, listens for online events).
   */
  private init(): void {
    if (this.isInitialized) return;

    this.startAutoFlush();
    this.isInitialized = true;

    console.log('[AnalyticsService] Initialized with deviceId:', this.deviceId);

    // Attempt initial flush after a short delay to allow app to settle
    setTimeout(() => this.flush(), 2000);

    // Manutenção one-time do IndexedDB (backfill pending_sync dos não-sync antigos
    // + limpeza do backlog acumulado). Chunked/yields — NÃO bloqueia playback.
    // Deferida pra rodar depois do app estabilizar. Fire-and-forget.
    setTimeout(() => {
      analyticsDB
        .runBackgroundMaintenance()
        .catch((err) =>
          console.warn('[AnalyticsService] Manutenção falhou:', err),
        );
    }, 8000);
  }

  /**
   * Gets or creates a persistent device ID.
   */
  private getOrCreateDeviceId(): string {
    try {
      const stored = localStorage.getItem(DEVICE_ID_KEY);
      if (stored) return stored;

      // Generate new device ID
      const id = this.generateDeviceId();
      localStorage.setItem(DEVICE_ID_KEY, id);
      return id;
    } catch {
      // localStorage might not be available
      return this.generateDeviceId();
    }
  }

  /**
   * Generates a unique device ID.
   */
  private generateDeviceId(): string {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 10);
    return `DEV-${timestamp}-${random}`.toUpperCase();
  }

  /**
   * Generates a unique event ID.
   */
  private generateEventId(): string {
    // Use the centralized UUID generator which handles secure/non-secure contexts
    return generateUUID();
  }

  private async getLiveBatteryInfo(): Promise<{
    batteryLevel: number | 'NA';
    isCharging: boolean | 'NA';
  }> {
    // 1. Try Capacitor Device API (Native Android/iOS)
    // Using dynamic import to match useBatteryStatus implementation
    try {
      const { Device } = await import('@capacitor/device');
      const info = await Device.getBatteryInfo();

      logger.debug('[AnalyticsService] Capacitor battery info:', info);

      if (info && typeof info.batteryLevel === 'number') {
        return {
          batteryLevel: Math.round(info.batteryLevel * 100),
          isCharging: info.isCharging || false,
        };
      }
    } catch (e) {
      // Ignore, might be web or plugin not available
    }

    // 2. Try Web Battery API (Browser/WebView fallback).
    // BatteryManager API não está na DOM lib oficial (deprecated em alguns
    // browsers) mas ainda funciona em Chrome/Edge Android — usamos cast
    // tipado em vez de `any`.
    type BatteryManagerLite = { level: number; charging: boolean };
    type NavWithBattery = Navigator & {
      getBattery?: () => Promise<BatteryManagerLite>;
    };
    try {
      const navWithBattery = navigator as NavWithBattery;
      if (typeof navWithBattery.getBattery === 'function') {
        const battery = await navWithBattery.getBattery();
        return {
          batteryLevel: Math.round(battery.level * 100),
          isCharging: battery.charging,
        };
      }
    } catch (e) {
      // Ignore
    }

    return { batteryLevel: 'NA', isCharging: 'NA' };
  }

  // ===========================================================================
  // TRACKING METHODS
  // ===========================================================================

  /**
   * Tracks a generic event.
   */
  async trackEvent(input: TrackEventInput): Promise<void> {
    const batteryInfo = await this.bateriaParaEvento();
    const event: AnalyticsEvent = {
      id: this.generateEventId(),
      deviceId: input.deviceId ?? this.deviceId,
      batteryLevel: batteryInfo.batteryLevel,
      isCharging: batteryInfo.isCharging,
      type: input.type,
      timestamp: new Date().toISOString(),
      data: input.data,
      synced: false,
    };

    // Fire and forget - don't block the caller
    analyticsDB
      .addEvent(event)
      .then(() => {
        this.eventosDesdeUltimoFlush += 1;
        if (this.deveEnviarAgora(input.type)) {
          this.flush();
        }
      })
      .catch((err) => {
        console.error('[AnalyticsService] Failed to store event:', err);
      });

    // Log in dev mode
    if (import.meta.env.DEV) {
      logger.debug('[AnalyticsService] Event tracked:', event.type, event.data);
    }
  }

  /**
   * Bateria para anexar ao evento, servida do cache.
   *
   * A primeira chamada ainda espera o nativo — o evento de `boot` é justamente
   * um em que o valor importa (classificação de crash por bateria), e nascer
   * `'NA'` perderia isso. Da segunda em diante é leitura de campo; quem
   * atualiza é o timer de {@link BATTERY_REFRESH_MS}.
   */
  private async bateriaParaEvento(): Promise<{
    batteryLevel: number | 'NA';
    isCharging: boolean | 'NA';
  }> {
    if (!this.bateriaJaLida) {
      this.bateriaAtual = await this.getLiveBatteryInfo();
      this.bateriaJaLida = true;
    }
    return this.bateriaAtual;
  }

  /**
   * O lote fecha agora? Chamado só pelo `trackEvent`.
   *
   * ⚠️ Este gate vive AQUI e não dentro do `flush()`. O `flush()` é o caminho
   * comum de oito chamadores — boot, volta-a-ficar-online, retry, dreno de
   * backlog encadeado, teto de tempo, trigger de SSE. Todos esperam que ele
   * envie o que existe. Um gate lá dentro quebraria os seis que não têm nada a
   * ver com a cadência de playback: o backlog pararia de drenar, e um player
   * que voltou da queda ficaria segurando eventos.
   */
  private deveEnviarAgora(tipo: AnalyticsEventType): boolean {
    if (IMMEDIATE_EVENT_TYPES.has(tipo)) return true;
    return this.eventosDesdeUltimoFlush >= BATCH_TRIGGER_EVENTS;
  }

  /**
   * Tracks when a media item starts playing.
   */
  trackPlaybackStart(
    mediaId: string,
    mediaName?: string,
    campaignId?: string | number,
  ): void {
    // Sem consumidor no servidor — vide TRACK_PLAYBACK_START.
    if (!TRACK_PLAYBACK_START) return;

    this.trackEvent({
      type: 'playback_start',
      data: {
        mediaId,
        mediaName,
        campanha_id:
          campaignId !== undefined
            ? typeof campaignId === 'number'
              ? campaignId
              : campaignId
            : undefined,
      },
    });
  }

  /**
   * Tracks when a media item finishes or is stopped.
   */
  trackPlaybackEnd(
    mediaId: string,
    duration: number,
    campaignId?: string | number,
  ): void {
    this.trackEvent({
      type: 'playback_end',
      data: {
        mediaId,
        duration,
        campanha_id:
          campaignId !== undefined
            ? typeof campaignId === 'number'
              ? campaignId
              : campaignId
            : undefined,
      },
    });
  }

  /**
   * Tracks when a video completes playback (plays to the end).
   * This is the primary metric for "play count".
   */
  /**
   * Último `playback_complete` emitido por `mediaId` (epoch ms). Guarda o
   * dedup de {@link DUP_COMPLETE_WINDOW_MS}. Podado quando passa de 64 entradas.
   */
  private lastCompleteAtByMedia = new Map<string, number>();

  trackPlaybackComplete(
    mediaId: string,
    duration: number,
    mediaName?: string,
    campaignId?: string | number,
    slot?: SlotCompletionContext,
    completedNaturally?: boolean,
  ): void {
    // Dedup na fonte: mata a duplicata da race do motor A/B (vide DUP_COMPLETE_WINDOW_MS).
    const now = Date.now();
    const last = this.lastCompleteAtByMedia.get(mediaId);
    if (last !== undefined && now - last < DUP_COMPLETE_WINDOW_MS) {
      logger.warn('[AnalyticsService] playback_complete duplicado suprimido', {
        mediaId,
        gapMs: now - last,
      });
      return;
    }
    this.lastCompleteAtByMedia.set(mediaId, now);
    if (this.lastCompleteAtByMedia.size > 64) {
      for (const [id, at] of this.lastCompleteAtByMedia) {
        if (now - at >= DUP_COMPLETE_WINDOW_MS)
          this.lastCompleteAtByMedia.delete(id);
      }
    }

    this.trackEvent({
      type: 'playback_complete',
      data: {
        mediaId,
        mediaName,
        duration,
        campanha_id:
          campaignId !== undefined
            ? typeof campaignId === 'number'
              ? campaignId
              : campaignId
            : undefined,
        // Contexto do slot (Tier 2/3) — vinha no `slot_completed`, que era
        // emitido 1:1 com este. Vide TRACK_SLOT_COMPLETED. No Tier 4 `slot` é
        // undefined e os campos não viajam: o agregador de SLA filtra por
        // jsonb_exists('playlistId'), então Tier 4 continua fora.
        slotIndex: slot?.slotIndex,
        playlistId: slot?.playlistId,
        programacaoId: slot?.programacaoId,
        tier: slot?.tier,
        // O explícito vence o do slot: em grupo exclusivo (Tier 1) NÃO existe contexto
        // de slot, e é justamente lá que o pulso do sync corta o item — sem este
        // parâmetro o corte chegaria ao servidor indistinguível de uma exibição cheia.
        completedNaturally: completedNaturally ?? slot?.completedNaturally,
      },
    });
  }

  /**
   * Tracks an error event.
   */
  trackError(errorCode: string, errorMessage: string): void {
    this.trackEvent({
      type: 'error',
      data: {
        errorCode,
        errorMessage,
      },
    });

    // Also send to LogRocket for session context
    logRocketService.trackError(errorMessage, { errorCode });
  }

  /**
   * Tracks a genuine playback failure (premature end, load/codec/decode error).
   * Additive telemetry — emitted alongside the existing playback flow, never
   * substitutes it. `mediaId` flows through `data` exactly like
   * trackPlaybackComplete, so the backend receives it as `media_id`.
   */
  trackPlaybackError(args: {
    mediaId: string;
    mediaName?: string;
    reason:
      | 'PREMATURE_END'
      | 'LOAD_ERROR'
      | 'CODEC_UNSUPPORTED'
      | 'AUDIO_DECODE_ERROR';
    durationActual?: number;
    durationNatural?: number;
    errorCode?: string;
    errorMessage?: string;
    [key: string]: unknown;
  }): void {
    // args already carries mediaId, which trackEvent → server maps to
    // media_id (same path as trackPlaybackComplete). Spread is sufficient.
    this.trackEvent({
      type: 'playback_error',
      data: {
        ...args,
        platform: navigator.userAgent,
      },
    });
  }

  /**
   * Tracks app boot.
   */
  trackBoot(): void {
    this.trackEvent({
      type: 'boot',
      data: {
        platform: navigator.userAgent,
        appVersion: import.meta.env.VITE_APP_VERSION || '1.0.0',
        timestamp: new Date().toISOString(),
      },
    });
  }

  /**
   * Tracks a sync event.
   */
  trackSync(success: boolean): void {
    this.trackEvent({
      type: 'sync',
      data: {
        success,
      },
    });
  }

  /**
   * Tracks when the active campaign changes.
   */
  trackCampaignChange(previousId: string | null, newId: string | null): void {
    this.trackEvent({
      type: 'campaign_change',
      data: {
        previousCampaignId: previousId ?? undefined,
        newCampaignId: newId ?? undefined,
      },
    });
  }

  // ===========================================================================
  // CP10 — Tier 2/3 slot_sequence events
  // ===========================================================================

  /**
   * CP10 — Tracks quando um slot da slot_sequence (Tier 2/3) inicia.
   * Roda ALONGSIDE trackPlaybackStart (não substitui) — carrega contexto
   * extra que o playback_start não tem (slotIndex, scheduledOffsetS, tier).
   * Backend usa pra dashboard "qual slot tocou, no horário previsto?".
   */
  trackSlotStarted(args: {
    slotIndex: number;
    scheduledOffsetS: number;
    playlistId?: number;
    programacaoId?: number;
    tier: number;
    contentId?: string;
    contentName?: string;
  }): void {
    // Sem consumidor no servidor — vide TRACK_SLOT_STARTED.
    if (!TRACK_SLOT_STARTED) return;

    this.trackEvent({
      type: 'slot_started',
      data: {
        slotIndex: args.slotIndex,
        scheduledOffsetS: args.scheduledOffsetS,
        playlistId: args.playlistId,
        programacaoId: args.programacaoId,
        tier: args.tier,
        mediaId: args.contentId,
        mediaName: args.contentName,
      },
    });
  }

  /**
   * CP10 — Tracks quando um slot da slot_sequence (Tier 2/3) termina.
   * `durationActual` = quanto realmente tocou; `completedNaturally` = true
   * se o conteúdo chegou ao fim, false se interrompido (trigger, sync, etc.).
   */
  trackSlotCompleted(args: {
    slotIndex: number;
    durationActual: number;
    completedNaturally: boolean;
    playlistId?: number;
    programacaoId?: number;
    tier: number;
    contentId?: string;
  }): void {
    // Unificado no playback_complete — vide TRACK_SLOT_COMPLETED.
    if (!TRACK_SLOT_COMPLETED) return;

    this.trackEvent({
      type: 'slot_completed',
      data: {
        slotIndex: args.slotIndex,
        durationActual: args.durationActual,
        completedNaturally: args.completedNaturally,
        playlistId: args.playlistId,
        programacaoId: args.programacaoId,
        tier: args.tier,
        mediaId: args.contentId,
      },
    });
  }

  /**
   * CP10 — Tracks quando o tier resolvido do player muda (ex: Tier 4 →
   * Tier 3 quando uma playlist é atribuída). Útil pra correlacionar
   * mudanças de comportamento no dashboard.
   */
  trackTierSwitch(fromTier: number, toTier: number, reason: string): void {
    this.trackEvent({
      type: 'tier_switch',
      data: {
        fromTier,
        toTier,
        reason,
      },
    });
  }

  // ===========================================================================
  // FLUSH / SYNC
  // ===========================================================================

  /**
   * Fase 1b — registra o callback que reconecta o SSE de conteúdo. Chamado no
   * boot (useSyncInit) apontando pra `syncStore.ensureSSEConnected`.
   */
  setSseReconnectHint(cb: () => void): void {
    this.sseReconnectHint = cb;
  }

  /**
   * Fase 1b — reage ao `sse_connected` da resposta do flush. `false` = o server
   * NÃO tem um subscriber SSE ativo pra este player (conexão zumbi) → força
   * reconexão. Throttle de 30s evita martelar se vários flushes seguidos
   * reportarem desconectado. `nowMs` injetável pra teste.
   *
   * Back-compat: server antigo não devolve o campo (`undefined`) → no-op.
   */
  handleSseConnectedHint(
    sseConnected: unknown,
    nowMs: number = Date.now(),
  ): void {
    if (sseConnected !== false) {
      return; // true, undefined (server antigo) ou não-boolean → nada a fazer
    }
    if (!this.sseReconnectHint) {
      return;
    }
    if (nowMs - this.lastSseReconnectHintAt < 30_000) {
      return; // throttle: no máximo 1 reconexão sugerida a cada 30s
    }
    this.lastSseReconnectHintAt = nowMs;
    console.warn(
      '[AnalyticsService] server reportou sse_connected=false → forçando reconexão do SSE de conteúdo',
    );
    try {
      this.sseReconnectHint();
    } catch (e) {
      console.error('[AnalyticsService] sseReconnectHint falhou:', e);
    }
  }

  /**
   * Checks if the device ID is temporary.
   */
  private isTemporaryId(id: string): boolean {
    return !id || id.startsWith('efflux-temp-') || id.startsWith('DEV-');
  }

  /**
   * Sends pending events to the server.
   */
  /**
   * Reage a leituras lentas sustentadas do IndexedDB.
   *
   * NÃO faz cirurgia: detecta, tenta o caminho não-destrutivo e AVISA o
   * servidor. A reconstrução automática do banco (apagar e recriar) existiu
   * aqui e foi REMOVIDA por decisão do Renato em 20/09/2026 — vide o
   * histórico do git.
   *
   * O motivo é que o quadro mudou. A causa raiz era modificar um campo
   * INDEXADO a cada evento, o que acumula tombstones no LevelDB; medido no
   * Tab A7: 673 ms contra 2 ms num banco onde o campo nunca foi modificado —
   * 335×. O `deleteSynced` eliminou essa causa, e a varredura da frota
   * (20/09) não achou NENHUM device degradado. Ou seja, a reconstrução era
   * cirurgia automática em 6000 tablets para uma doença que ninguém tem e
   * que já foi prevenida — com três modos de falha reais, incluindo um que
   * matava o envio de analytics em silêncio.
   *
   * Se algum device degradar mesmo assim, é este aviso que nos conta, e a
   * decisão de intervir passa a ser nossa, não do tablet.
   */
  private async reagirSeDegradado(readMs: number): Promise<void> {
    if (readMs < DEGRADED_READ_MS) {
      this.slowReadStreak = 0;
      this.avisouNesteEpisodio = false; // episódio encerrado
      return;
    }

    this.slowReadStreak += 1;
    console.warn(
      `[AnalyticsService] Leitura do lote levou ${readMs}ms (${this.slowReadStreak}/${DEGRADED_STREAK} seguidas)`,
    );
    if (this.slowReadStreak < DEGRADED_STREAK) return;
    if (this.avisouNesteEpisodio) return; // um aviso por episódio

    this.avisouNesteEpisodio = true;
    this.slowReadStreak = 0;

    // Recibo pro servidor: é assim que descobrimos, no admin, que um device
    // está degradando — sem depender de alguém plugar um cabo nele.
    this.trackError(
      'ANALYTICS_DB_SLOW',
      `IndexedDB lento: ${DEGRADED_STREAK} leituras acima de ${DEGRADED_READ_MS}ms (última: ${readMs}ms).`,
    );
  }

  async flush(): Promise<void> {
    if (this.isFlushing) {
      // Concorrência interna esperada (timer + trigger SSE podem coincidir).
      // Não informativo pro operador — DEBUG.
      logger.debug('[AnalyticsService] Flush already in progress, skipping');
      return;
    }

    // Block flush if using temporary ID to prevent database pollution
    if (this.isTemporaryId(this.deviceId)) {
      if (this.isInitialized) {
        // Only log if service is fully up to avoid noise
        console.log(
          `[AnalyticsService] Skipping flush - temporary Device ID (${this.deviceId}). Waiting for valid auth.`,
        );
      }
      return;
    }

    this.isFlushing = true;
    // Zera o lote no ponto em que o envio realmente começa — e não nos returns
    // acima (já-em-andamento, id temporário), que não são tentativa de envio:
    // zerar ali faria o contador nunca chegar em BATCH_TRIGGER_EVENTS enquanto
    // o device não tem id válido, e o teto de tempo viraria o único gatilho.
    this.eventosDesdeUltimoFlush = 0;
    this.ultimoFlushEm = Date.now();

    try {
      const batchSize = this.nextBatchSize;
      const readStartedAt = Date.now();
      const events = await analyticsDB.getUnsyncedEvents(batchSize);
      await this.reagirSeDegradado(Date.now() - readStartedAt);

      // Lote cheio = a página do índice encheu, então quase certamente há mais
      // pendentes. Substituiu a contagem pelo banco que rodava no fim de todo
      // flush: numa base com 44k pendentes ele custava 59s POR CICLO — mais
      // caro que o envio inteiro, e só pra responder "encadeia ou não?".
      const likelyHasMore = events.length >= batchSize;

      if (events.length === 0) {
        this.nextBatchSize = BATCH_SIZE;
        return;
      }

      const { validEvents, invalidIds } = this.sanitizeEvents(events);
      if (invalidIds.length > 0) {
        console.warn(
          '[AnalyticsService] Dropping invalid analytics events:',
          invalidIds.length,
        );
        // Inválidos nunca serão aceitos pelo servidor — apaga em vez de
        // deixar marcado como "sincronizado" ocupando a base.
        await analyticsDB.deleteSynced(invalidIds);
      }

      if (validEvents.length === 0) {
        // Lote inteiro descartado por inválido — os ids já foram marcados como
        // sincronizados acima, então encadeia pra continuar drenando.
        if (likelyHasMore) setTimeout(() => this.flush(), 1000);
        return;
      }

      // Operação rotineira a cada 5min — DEBUG. Erros do flush ficam em
      // WARN/ERROR mais abaixo.
      logger.debug(
        `[AnalyticsService] Flushing ${validEvents.length} events for device ${this.deviceId}`,
      );

      const apiKey = localStorage.getItem('efflux_device_api_key');
      const headers: Record<string, string> = {
        'X-Device-Id': this.deviceId,
        'X-Device-Token': deviceTokenService.getToken(),
      };
      if (apiKey && apiKey.trim()) {
        headers['X-API-Key'] = apiKey;
      }

      // Transform events to match backend expectations (Snake Case for some fields)
      // Backend uses global SNAKE_CASE strategy, but deviceId has @JsonProperty("deviceId")
      const eventsToSend = validEvents.map((e) => ({
        ...e,
        deviceId: e.deviceId || this.deviceId, // Ensure deviceId is set
        // Map camelCase to snake_case for fields without @JsonProperty overrides in backend
        battery_level: e.batteryLevel,
        is_charging: e.isCharging,
      }));

      // Send to server (Java Content API)
      try {
        const postStartedAt = Date.now();
        const resp = await axios.post(
          `${this.apiUrl}/api/analytics/events`,
          { events: eventsToSend },
          {
            headers,
            timeout: FLUSH_TIMEOUT_MS, // 60s — cobre cold-start + contention em dev
          },
        );

        // Fase 1b — piggyback: se o server reporta que NÃO tem o SSE deste
        // player (conexão zumbi), reconecta na hora. Back-compat: server antigo
        // não devolve o campo → no-op.
        this.handleSseConnectedHint(
          (resp?.data as { sse_connected?: unknown } | undefined)
            ?.sse_connected,
        );

        // Servidor confirmou → apaga. Nada no player lê evento já
        // sincronizado (vide AnalyticsDB.deleteSynced).
        const ids = validEvents.map((e) => e.id);
        await analyticsDB.deleteSynced(ids);

        // Teto de acúmulo — NÃO a cada flush. Durante um backlog o que importa
        // é vazão: cada ciclo poupado é mais um lote de 25 subindo. É
        // manutenção, não caminho crítico.
        this.flushesSincePurge += 1;
        if (this.flushesSincePurge >= PURGE_EVERY_N_FLUSHES) {
          this.flushesSincePurge = 0;
          // Teto do acúmulo, por QUANTIDADE (Renato 20/09/2026): acima de
          // 50.000 registros sem sincronizar, os mais antigos são descartados.
          // Não há regra de idade — vide AnalyticsDB.enforceRetentionLimits.
          const descarte = await analyticsDB.enforceRetentionLimits();
          if (descarte.deleted > 0) {
            // Recibo do que foi perdido. Não é alerta de acúmulo — esse é
            // impossível (se acumulou, não há servidor pra avisar). Este
            // evento nasce agora e sobe no próximo flush: é como o operador
            // descobre POR QUE faltam impressões daquele player naquela
            // janela, em vez de ver um buraco sem explicação.
            this.trackError(
              'ANALYTICS_RETENTION_DISCARD',
              `${descarte.deleted} eventos descartados: acúmulo passou do teto (janela perdida ${descarte.oldest} → ${descarte.newest}); restam ${descarte.total}`,
            );
          }
        }

        // Sucesso → reseta contador de retry (próximo erro começa o backoff do zero)
        this.retryAttempts = 0;

        // Rajada adaptativa: só acelera enquanto HÁ acúmulo, e recua sozinha
        // se o servidor começar a demorar (protege o @Transactional(timeout=30)
        // sem precisar adivinhar um teto fixo). Vide BACKLOG_BATCH_MAX.
        const postElapsedMs = Date.now() - postStartedAt;
        if (!likelyHasMore) {
          this.nextBatchSize = BATCH_SIZE;
        } else if (postElapsedMs > BACKLOG_LATENCY_CEILING_MS) {
          this.nextBatchSize = Math.max(BATCH_SIZE, Math.floor(batchSize / 2));
          console.warn(
            `[AnalyticsService] Servidor levou ${postElapsedMs}ms pra ${validEvents.length} eventos — lote ${batchSize} → ${this.nextBatchSize}`,
          );
        } else {
          this.nextBatchSize = Math.min(BACKLOG_BATCH_MAX, batchSize * 2);
        }

        if (likelyHasMore) {
          // Encadeia imediatamente enquanto a página do índice vier cheia.
          setTimeout(() => this.flush(), 1000);
        }
      } catch (err) {
        // Network error - events will be synced later.
        // Renato 2026-06-12: rebaixa pra INFO quando device está pending
        // (401 esperado até admin aprovar — sem ruído vermelho no console).
        logAuthAwareError(
          'warn',
          '[AnalyticsService]',
          'Failed to send events to server:',
          err,
        );
        // Erro (timeout do servidor inclusive) → recua o lote pela metade antes
        // do retry. Se a rajada foi longe demais, ela se corrige sozinha em vez
        // de bater no mesmo teto repetidamente.
        this.nextBatchSize = Math.max(BATCH_SIZE, Math.floor(batchSize / 2));

        // Backoff exponencial: 30s → 60s → 120s → 300s (cap)
        // Antes era fixo em 30s, o que martelava o backend já sob pressão
        // e impedia a recuperação.
        const idx = Math.min(
          this.retryAttempts,
          RETRY_BACKOFF_SEQUENCE_MS.length - 1,
        );
        const retryDelay = RETRY_BACKOFF_SEQUENCE_MS[idx];
        this.retryAttempts++;
        // Não polui console com retry-counter quando device está pending —
        // info logo acima já cobre o contexto.
        if (!isWaitingDeviceApproval()) {
          console.warn(
            `[AnalyticsService] Retry #${this.retryAttempts} agendado em ${retryDelay / 1000}s`,
          );
        }
        setTimeout(() => {
          if (!this.isFlushing) this.flush();
        }, retryDelay);
      }
    } catch (err) {
      console.error('[AnalyticsService] Flush failed:', err);
    } finally {
      this.isFlushing = false;
    }
  }

  /**
   * Returns the count of events waiting to be synced.
   */

  private sanitizeEvents(events: AnalyticsEvent[]): {
    validEvents: AnalyticsEvent[];
    invalidIds: string[];
  } {
    const validEvents: AnalyticsEvent[] = [];
    const invalidIds: string[] = [];

    for (const event of events) {
      if (!event || !event.id || !event.type || !event.timestamp) {
        if (event?.id) {
          invalidIds.push(event.id);
        }
        continue;
      }

      const data = event.data;
      if (!data || typeof data !== 'object' || Array.isArray(data)) {
        invalidIds.push(event.id);
        continue;
      }

      const normalized: AnalyticsEvent = { ...event, data: { ...data } };
      if (normalized.deviceId == null || normalized.deviceId === '') {
        normalized.deviceId = this.deviceId;
      }
      if (normalized.data.duration !== undefined) {
        const duration = Number(normalized.data.duration);
        if (Number.isFinite(duration)) {
          normalized.data.duration = duration;
        } else {
          delete normalized.data.duration;
        }
      }
      validEvents.push(normalized);
    }

    return { validEvents, invalidIds };
  }

  // ===========================================================================
  // AUTO-FLUSH MANAGEMENT
  // ===========================================================================

  /**
   * Arma o teto de tempo do lote e o listener de volta-online.
   *
   * O timer tica a cada {@link CEILING_CHECK_MS} mas só envia quando passou do
   * teto desde a última tentativa — vide {@link CEILING_CHECK_MS} para por que
   * não é um `setInterval` na duração do teto.
   */
  private startAutoFlush(): void {
    this.stopAutoFlush();

    this.flushInterval = setInterval(() => {
      // Nada acumulado: não acorda o banco nem a rede só para constatar isso.
      // Sem esta guarda, um player parado faria uma leitura de IndexedDB a cada
      // tique, para sempre.
      if (this.eventosDesdeUltimoFlush === 0) return;
      if (Date.now() - this.ultimoFlushEm < this.tetoComJitter) return;
      this.flush();
    }, CEILING_CHECK_MS);

    // Releitura da bateria fora do caminho quente — vide BATTERY_REFRESH_MS.
    // Best-effort: falha aqui mantém o último valor bom, nunca derruba evento.
    this.bateriaTimer = setInterval(() => {
      this.getLiveBatteryInfo()
        .then((info) => {
          this.bateriaAtual = info;
          this.bateriaJaLida = true;
        })
        .catch(() => {
          /* mantém o último snapshot */
        });
    }, BATTERY_REFRESH_MS);

    // Flush when coming online
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline);
    }

    console.log(
      `[AnalyticsService] Lote armado: ${BATCH_TRIGGER_EVENTS} eventos ou ${Math.round(
        this.tetoComJitter / 1000,
      )}s (teto ${FLUSH_CEILING_MS / 1000}s ± ${CEILING_JITTER * 100}%)`,
    );
  }

  /**
   * Stops auto-flush.
   */
  private stopAutoFlush(): void {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }

    // startAutoFlush() chama stopAutoFlush() antes de rearmar, então sem isto
    // cada rearme deixaria um timer de bateria órfão martelando a ponte JNI.
    if (this.bateriaTimer) {
      clearInterval(this.bateriaTimer);
      this.bateriaTimer = null;
    }

    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleOnline);
    }
  }

  /**
   * Handler for when the app comes online.
   */
  private handleOnline = (): void => {
    console.log('[AnalyticsService] Online detected - triggering flush');
    this.flush();
  };

  // ===========================================================================
  // LIFECYCLE
  // ===========================================================================

  /**
   * Cleans up the service.
   */
  destroy(): void {
    this.stopAutoFlush();
    this.isInitialized = false;
    console.log('[AnalyticsService] Destroyed');
  }

  /**
   * Gets the device ID.
   */
  /**
   * Reporta os crashes capturados no boot anterior que ainda não subiram
   * (silent_crash / error / unhandledrejection). Chamado 1× após o boot
   * estabilizar (fora do caminho crítico).
   *
   * REUSA o pipeline de analytics (`trackEvent` → fila IndexedDB → flush com
   * retry/offline) — o crash vira um evento `player_crash` em
   * `/api/analytics/events`. O server dispara o alerta `PLAYER_CRASH` ao ingerir
   * esse tipo. Sem endpoint novo. Marca reportado logo após enfileirar (a fila é
   * durável — sobrevive a reboot), garantindo enqueue exactly-once por crash.
   * NÃO reporta com device id temporário (espera auth válida).
   */
  reportPendingCrashes(): void {
    if (this.isTemporaryId(this.deviceId)) return;

    const crashes = getUnreportedCrashes();
    if (crashes.length === 0) return;

    const appVersion = import.meta.env.VITE_APP_VERSION || '1.0.0';
    let platform = 'unknown';
    try {
      platform = detectPlatform();
    } catch {
      /* mantém 'unknown' */
    }

    logger.warn(
      `[CrashReporter] Enfileirando ${crashes.length} crash(es) do boot anterior (evento player_crash via analytics)`,
    );

    for (const c of crashes) {
      this.trackEvent({
        type: 'player_crash',
        data: {
          crash_type: c.type,
          message: c.message,
          occurred_at: c.occurredAt ?? c.timestamp,
          detected_at: c.timestamp,
          confidence: c.confidence,
          app_version: appVersion,
          platform,
          userAgent: navigator.userAgent,
          ...(c.stack ? { stack: c.stack } : {}),
          ...(c.context ?? {}),
        },
      });
      markCrashesReported(c.timestamp);
    }
  }

  getDeviceId(): string {
    return this.deviceId;
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();
export default analyticsService;
